import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  Plus, Edit2, Trash2, Save, X, Loader2, ChevronUp, ChevronDown,
  Star, Check, Minus, Eye, EyeOff, Sparkles, DollarSign, ExternalLink,
} from 'lucide-react';
import clsx from 'clsx';

const API = process.env.REACT_APP_API_URL || '';
const MARKETING_URL = process.env.REACT_APP_MARKETING_URL || 'https://rostery.com.au';

const EMPTY = {
  name: '',
  subtitle: '',
  price_monthly: 0,
  price_annual: 0,
  currency: 'AUD',
  cta_text: 'Get Started',
  cta_url: 'https://app.rostery.com.au/register',
  is_highlighted: false,
  badge: '',
  features: [],
  sort_order: 0,
  is_active: true,
};

/* ─────────────────── Feature row in modal ───────────────────── */
function FeatureRow({ feature, onChange, onRemove }) {
  return (
    <div className="flex items-center gap-2.5 group">
      <button
        type="button"
        onClick={() => onChange({ ...feature, included: !feature.included })}
        className={clsx(
          'w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-all',
          feature.included
            ? 'bg-teal-500 border-teal-500 text-white'
            : 'border-slate-300 text-transparent hover:border-slate-400'
        )}
      >
        <Check size={12} strokeWidth={3} />
      </button>
      <input
        type="text"
        value={feature.text}
        onChange={(e) => onChange({ ...feature, text: e.target.value })}
        placeholder="Describe this feature…"
        className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-colors"
      />
      <button
        type="button"
        onClick={onRemove}
        className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
      >
        <X size={13} />
      </button>
    </div>
  );
}

/* ─────────────────── Plan Modal ─────────────────────────────── */
function PlanModal({ plan, allPlans, onClose, onSave, saving }) {
  const [form, setForm] = useState(plan ? { ...plan } : { ...EMPTY });

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setNum = (key) => (e) => setForm((f) => ({ ...f, [key]: parseFloat(e.target.value) || 0 }));
  const setBool = (key) => (e) => {
    const val = e.target.checked;
    if (key === 'is_highlighted' && val) {
      const existing = allPlans.find((p) => p.is_highlighted && p.id !== form.id);
      if (existing) {
        toast(`"${existing.name}" is currently highlighted — saving will replace it`, {
          icon: '⚠️',
          duration: 4000,
        });
      }
    }
    setForm((f) => ({ ...f, [key]: val }));
  };

  function addFeature() {
    setForm((f) => ({ ...f, features: [...f.features, { text: '', included: true }] }));
  }
  function updateFeature(i, val) {
    setForm((f) => {
      const a = [...f.features];
      a[i] = val;
      return { ...f, features: a };
    });
  }
  function removeFeature(i) {
    setForm((f) => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));
  }

  const inputCls =
    'w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-colors';
  const labelCls = 'block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
              <DollarSign size={16} className="text-primary-600" />
            </div>
            <h2 className="font-bold text-slate-900">{plan ? 'Edit Plan' : 'New Pricing Plan'}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Name + subtitle */}
          <div>
            <label className={labelCls}>Plan Name <span className="text-red-400 normal-case font-normal">*</span></label>
            <input type="text" value={form.name} onChange={setField('name')} className={inputCls} placeholder="e.g. Starter, Growth, Enterprise" />
          </div>
          <div>
            <label className={labelCls}>Subtitle</label>
            <input type="text" value={form.subtitle || ''} onChange={setField('subtitle')} className={inputCls} placeholder="For growing NDIS providers" />
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Monthly (${form.currency || 'AUD'})</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                <input type="number" value={form.price_monthly} onChange={setNum('price_monthly')} className={`${inputCls} pl-7`} min="0" step="0.01" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Annual/mo (billed yearly)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                <input type="number" value={form.price_annual} onChange={setNum('price_annual')} className={`${inputCls} pl-7`} min="0" step="0.01" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Currency</label>
              <input type="text" value={form.currency} onChange={setField('currency')} className={inputCls} placeholder="AUD" />
            </div>
          </div>

          {/* CTA */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>CTA Button Text</label>
              <input type="text" value={form.cta_text} onChange={setField('cta_text')} className={inputCls} placeholder="Get Started" />
            </div>
            <div>
              <label className={labelCls}>CTA Button URL</label>
              <input type="text" value={form.cta_url} onChange={setField('cta_url')} className={inputCls} />
            </div>
          </div>

          {/* Badge + sort */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Badge Text <span className="text-slate-400 normal-case font-normal">(optional)</span></label>
              <input type="text" value={form.badge || ''} onChange={setField('badge')} className={inputCls} placeholder="Most Popular" />
            </div>
            <div>
              <label className={labelCls}>Sort Order</label>
              <input type="number" value={form.sort_order} onChange={setNum('sort_order')} className={inputCls} min="0" />
            </div>
          </div>

          {/* Toggles */}
          <div className="flex items-center gap-3">
            <label
              className={clsx(
                'flex-1 flex items-center gap-3 cursor-pointer p-3 rounded-xl border-2 transition-all',
                form.is_highlighted
                  ? 'border-indigo-300 bg-indigo-50'
                  : 'border-slate-200 hover:border-slate-300'
              )}
            >
              <input type="checkbox" checked={form.is_highlighted} onChange={setBool('is_highlighted')} className="sr-only" />
              <div
                className={clsx(
                  'w-9 h-5 rounded-full transition-colors shrink-0',
                  form.is_highlighted ? 'bg-indigo-500' : 'bg-slate-300'
                )}
              >
                <div
                  className={clsx(
                    'w-4 h-4 bg-white rounded-full shadow mt-0.5 transition-transform',
                    form.is_highlighted ? 'translate-x-4 ml-0.5' : 'translate-x-0 ml-0.5'
                  )}
                />
              </div>
              <div>
                <p className="text-xs font-bold text-slate-700">Highlighted</p>
                <p className="text-[10px] text-slate-400">Dark navy card</p>
              </div>
            </label>
            <label
              className={clsx(
                'flex-1 flex items-center gap-3 cursor-pointer p-3 rounded-xl border-2 transition-all',
                form.is_active
                  ? 'border-teal-300 bg-teal-50'
                  : 'border-slate-200 hover:border-slate-300'
              )}
            >
              <input type="checkbox" checked={form.is_active} onChange={setBool('is_active')} className="sr-only" />
              <div
                className={clsx(
                  'w-9 h-5 rounded-full transition-colors shrink-0',
                  form.is_active ? 'bg-teal-500' : 'bg-slate-300'
                )}
              >
                <div
                  className={clsx(
                    'w-4 h-4 bg-white rounded-full shadow mt-0.5 transition-transform',
                    form.is_active ? 'translate-x-4 ml-0.5' : 'translate-x-0 ml-0.5'
                  )}
                />
              </div>
              <div>
                <p className="text-xs font-bold text-slate-700">Active</p>
                <p className="text-[10px] text-slate-400">Shown on website</p>
              </div>
            </label>
          </div>

          {/* Features */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className={labelCls}>Features / Inclusions</p>
                <p className="text-xs text-slate-400 -mt-1">Check = included, uncheck = not included</p>
              </div>
              <button
                type="button"
                onClick={addFeature}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-teal-50 text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors font-semibold"
              >
                <Plus size={12} /> Add Feature
              </button>
            </div>
            <div className="space-y-2">
              {form.features.map((f, i) => (
                <FeatureRow key={i} feature={f} onChange={(val) => updateFeature(i, val)} onRemove={() => removeFeature(i)} />
              ))}
              {form.features.length === 0 && (
                <div className="text-center py-6 border-2 border-dashed border-slate-200 rounded-xl">
                  <p className="text-sm text-slate-400 font-medium">No features added yet</p>
                  <p className="text-xs text-slate-300 mt-0.5">Click "Add Feature" to build the feature list</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 shrink-0">
          <button onClick={onClose} className="px-4 py-2.5 text-sm text-slate-600 font-medium hover:text-slate-800 transition-colors">
            Cancel
          </button>
          <button
            onClick={() => onSave(form)}
            disabled={saving || !form.name}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            {plan ? 'Save Changes' : 'Create Plan'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── Plan Card ──────────────────────────────── */
function PlanCard({ plan, isFirst, isLast, onEdit, onDelete, onMoveUp, onMoveDown }) {
  const isHighlighted = plan.is_highlighted;

  return (
    <div
      className={clsx(
        'rounded-2xl overflow-hidden border shadow-sm transition-all',
        isHighlighted
          ? 'border-indigo-600 shadow-indigo-100 shadow-md scale-[1.01]'
          : 'border-slate-200 hover:shadow-md',
        !plan.is_active && 'opacity-60'
      )}
    >
      {/* Card header */}
      <div
        className={clsx(
          'px-6 pt-6 pb-5 relative',
          isHighlighted ? 'bg-slate-900 text-white' : 'bg-white'
        )}
      >
        {/* Badge */}
        {(plan.badge || plan.is_highlighted) && (
          <div className="flex items-center gap-2 mb-3">
            {plan.is_highlighted && (
              <span className="flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full bg-indigo-500 text-white">
                <Sparkles size={9} /> HIGHLIGHTED
              </span>
            )}
            {plan.badge && (
              <span className={clsx(
                'text-[10px] font-bold px-2.5 py-1 rounded-full',
                isHighlighted ? 'bg-white/15 text-white' : 'bg-teal-50 text-teal-700 border border-teal-200'
              )}>
                {plan.badge}
              </span>
            )}
            {!plan.is_active && (
              <span className={clsx(
                'text-[10px] font-bold px-2.5 py-1 rounded-full',
                isHighlighted ? 'bg-white/10 text-white/70' : 'bg-slate-100 text-slate-400'
              )}>
                Inactive
              </span>
            )}
          </div>
        )}
        {!plan.badge && !plan.is_highlighted && !plan.is_active && (
          <div className="flex mb-3">
            <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-400">
              Inactive
            </span>
          </div>
        )}

        {/* Name */}
        <h3 className={clsx('text-xl font-bold', isHighlighted ? 'text-white' : 'text-slate-900')}>
          {plan.name}
        </h3>

        {/* Price */}
        <div className="mt-2 flex items-baseline gap-1.5">
          {plan.price_monthly === 0 ? (
            <span className={clsx('text-2xl font-bold', isHighlighted ? 'text-white' : 'text-slate-900')}>
              Custom
            </span>
          ) : (
            <>
              <span className={clsx('text-3xl font-extrabold', isHighlighted ? 'text-white' : 'text-slate-900')}>
                ${plan.price_monthly}
              </span>
              <span className={clsx('text-sm', isHighlighted ? 'text-white/60' : 'text-slate-400')}>
                /{plan.currency || 'AUD'} /mo
              </span>
            </>
          )}
        </div>
        {plan.price_annual > 0 && plan.price_monthly > 0 && (
          <p className={clsx('text-xs mt-0.5', isHighlighted ? 'text-white/50' : 'text-slate-400')}>
            ${plan.price_annual}/mo billed annually
          </p>
        )}

        {/* Subtitle */}
        {plan.subtitle && (
          <p className={clsx('text-sm mt-2', isHighlighted ? 'text-white/70' : 'text-slate-500')}>
            {plan.subtitle}
          </p>
        )}
      </div>

      {/* Features */}
      <div className={clsx('px-6 py-4 border-t', isHighlighted ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-100')}>
        {plan.features && plan.features.length > 0 ? (
          <ul className="space-y-2">
            {plan.features.map((feat, i) => (
              <li key={i} className={clsx('flex items-start gap-2.5 text-sm', !feat.included && 'opacity-50')}>
                <span
                  className={clsx(
                    'mt-0.5 w-4 h-4 rounded-full flex items-center justify-center shrink-0',
                    feat.included
                      ? isHighlighted ? 'bg-teal-400' : 'bg-teal-500'
                      : isHighlighted ? 'bg-slate-600' : 'bg-slate-200'
                  )}
                >
                  {feat.included ? (
                    <Check size={10} className="text-white" strokeWidth={3} />
                  ) : (
                    <Minus size={10} className={isHighlighted ? 'text-slate-400' : 'text-slate-400'} strokeWidth={3} />
                  )}
                </span>
                <span className={clsx(isHighlighted ? 'text-white/80' : 'text-slate-600')}>{feat.text}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={clsx('text-xs italic', isHighlighted ? 'text-white/40' : 'text-slate-400')}>
            No features listed
          </p>
        )}
      </div>

      {/* Footer actions */}
      <div
        className={clsx(
          'px-5 py-3 border-t flex items-center justify-between gap-2',
          isHighlighted ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-100'
        )}
      >
        {/* Sort controls */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => onMoveUp(plan)}
            disabled={isFirst}
            className={clsx(
              'p-1.5 rounded-lg transition-colors disabled:opacity-25',
              isHighlighted
                ? 'text-white/50 hover:text-white hover:bg-white/10'
                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
            )}
          >
            <ChevronUp size={14} />
          </button>
          <button
            onClick={() => onMoveDown(plan)}
            disabled={isLast}
            className={clsx(
              'p-1.5 rounded-lg transition-colors disabled:opacity-25',
              isHighlighted
                ? 'text-white/50 hover:text-white hover:bg-white/10'
                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
            )}
          >
            <ChevronDown size={14} />
          </button>
          <span className={clsx('text-xs ml-1', isHighlighted ? 'text-white/30' : 'text-slate-300')}>
            {plan.features?.length || 0} features
          </span>
        </div>

        {/* Edit / Delete */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(plan)}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors',
              isHighlighted
                ? 'text-white border-white/20 hover:bg-white/10'
                : 'text-slate-600 border-slate-200 hover:bg-slate-50'
            )}
          >
            <Edit2 size={12} /> Edit
          </button>
          <button
            onClick={() => onDelete(plan.id)}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors',
              isHighlighted
                ? 'text-red-400 border-red-400/30 hover:bg-red-900/30'
                : 'text-red-500 border-red-200 hover:bg-red-50'
            )}
          >
            <Trash2 size={12} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── Main Page ──────────────────────────────── */
export default function PricingPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null); // null | 'new' | plan object
  const token = localStorage.getItem('token');

  async function fetchPlans() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/marketing/pricing-plans/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setPlans(Array.isArray(data) ? data.sort((a, b) => a.sort_order - b.sort_order) : []);
    } catch {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchPlans(); }, []);

  async function savePlan(form) {
    setSaving(true);
    try {
      const isNew = !form.id;
      const res = await fetch(
        isNew ? `${API}/api/marketing/pricing-plans` : `${API}/api/marketing/pricing-plans/${form.id}`,
        {
          method: isNew ? 'POST' : 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(form),
        }
      );
      if (!res.ok) throw new Error();
      toast.success(isNew ? 'Plan created' : 'Plan updated');
      setModal(null);
      fetchPlans();
    } catch {
      toast.error('Failed to save plan');
    } finally {
      setSaving(false);
    }
  }

  async function deletePlan(id) {
    if (!window.confirm('Delete this pricing plan? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API}/api/marketing/pricing-plans/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Plan deleted');
      fetchPlans();
    } catch {
      toast.error('Failed to delete plan');
    }
  }

  async function move(id, dir) {
    const idx = plans.findIndex((p) => p.id === id);
    const target = plans[idx + dir];
    if (!target) return;
    await Promise.all([
      fetch(`${API}/api/marketing/pricing-plans/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...plans[idx], sort_order: target.sort_order }),
      }),
      fetch(`${API}/api/marketing/pricing-plans/${target.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...target, sort_order: plans[idx].sort_order }),
      }),
    ]);
    fetchPlans();
  }

  const highlighted = plans.find((p) => p.is_highlighted);

  return (
    <div className="p-6 space-y-6">
      {modal !== null && (
        <PlanModal
          plan={modal === 'new' ? null : modal}
          allPlans={plans}
          onClose={() => setModal(null)}
          onSave={savePlan}
          saving={saving}
        />
      )}

      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900">Pricing Plans</h1>
            {plans.length > 0 && (
              <span className="px-2.5 py-0.5 text-xs font-bold text-primary-700 bg-primary-50 border border-primary-200 rounded-full">
                {plans.length} plan{plans.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <p className="text-slate-500 text-sm mt-1">
            Manage the pricing tiers shown on the marketing website
            {highlighted && (
              <span className="ml-2 text-indigo-600 font-medium">· "{highlighted.name}" is highlighted</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href={`${MARKETING_URL}/pricing`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-teal-600 border border-teal-200 rounded-xl hover:bg-teal-50 transition-colors">
            <ExternalLink size={14} />
            Preview on Site
          </a>
          <button
            onClick={() => setModal('new')}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 transition-colors shadow-sm"
          >
            <Plus size={16} /> Add Plan
          </button>
        </div>
      </div>

      {/* Plans grid */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : plans.length === 0 ? (
        <div className="text-center py-24 text-slate-400 border-2 border-dashed border-slate-200 rounded-2xl">
          <DollarSign size={40} className="mx-auto mb-3 opacity-20" />
          <p className="text-lg font-semibold text-slate-500">No pricing plans yet</p>
          <p className="text-sm mt-1">Click "Add Plan" to create your first pricing tier</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 items-start">
          {plans.map((plan, idx) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              isFirst={idx === 0}
              isLast={idx === plans.length - 1}
              onEdit={setModal}
              onDelete={deletePlan}
              onMoveUp={() => move(plan.id, -1)}
              onMoveDown={() => move(plan.id, 1)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
